import { api, authFetch } from '@onvice/portal-shell-api';

export interface TestRun {
  id: string;
  name: string;
  started_at: string;
  started_by: string;
  status: 'active' | 'closed';
  source_files: string[];
  tester_ids: string[];
  tester_changelog: string | null;
}

export interface TestFinding {
  id: string;
  source_file: string;
  idx: number;
  title: string;
  feature_text: string;
  expectation: string | null;
  test_path: string | null;
  persona_key: string | null;
  triage_status: 'open' | 'release' | 'discard' | 'clarify';
  triaged_at: string | null;
  triage_note: string | null;
  // Eigenes Resultat (nur in Tester-Sicht gefuellt)
  my_status?: 'open' | 'pass' | 'fail' | null;
  my_comment?: string | null;
  my_admin_comment?: string | null;
  my_screenshot?: string | null;
  my_updated_at?: string | null;
}

export interface TesterResult {
  result_id?: string;
  tester_id: string;
  tester_email: string;
  tester_name: string;
  status: 'open' | 'pass' | 'fail';
  comment: string | null;
  admin_comment: string | null;
  screenshot_path: string | null;
  updated_at: string;
}

export interface AggregateFinding extends TestFinding {
  results: TesterResult[];
}

export interface RunStats {
  totals: {
    total: number;
    untriaged: number;
    released: number;
    discarded: number;
    clarify: number;
  };
  consensus: {
    all_pass: number;
    any_fail: number;
    no_results: number;
    partial: number;
  };
  per_tester: Array<{
    tester_id: string;
    tester_name: string;
    tester_email: string;
    pass: number;
    fail: number;
    skip: number;
  }>;
}

export interface CloneRunResult {
  id: string;
  name: string;
  started_at: string;
  finding_count: number;
  filter: 'all' | 'open' | 'fail' | 'unreleased';
}

export interface BundleResetResult {
  counts: Array<{ typ: string; n: number }>;
}

export interface ScreenshotCleanupResult {
  scanned: number;
  deleted: number;
  kept: number;
  errors: string[];
}

export interface Tester {
  id: string;
  email: string;
  name: string;
}

export interface VaultParseWarning {
  source_file: string;
  idx: number;
  title: string;
  status: string;
  reason: string;
}

export interface RunFromVaultResult {
  id: string;
  name: string;
  started_at: string;
  finding_count: number;
  obsolete_count: number;
  warnings: VaultParseWarning[];
}

export interface FindingFixVersion {
  version: number;
  fix_text: string;
  commit_hash: string | null;
  fix_date: string | null;
}
export interface FindingAdminComment {
  id: string;
  comment_text: string;
  comment_date: string | null;
  sort_order: number;
}
export interface FindingTestPath {
  id: string;
  stage: string;
  persona_key: string | null;
  url_path: string;
  hint: string | null;
  expectation: string | null;
  sort_order: number;
}
export interface MasterFinding {
  id: string;
  source_label: string;
  idx: number;
  title: string;
  original_text: string | null;
  original_date: string | null;
  status: 'offen' | 'in Verfeinerung' | 'final' | 'obsolete';
  replaced_by_id: string | null;
  created_at: string;
  updated_at: string;
  fix_versions: FindingFixVersion[];
  admin_comments: FindingAdminComment[];
  test_paths: FindingTestPath[];
}

export interface VaultFileEntry {
  name: string;
  size: number;
  mtime: string | null;
  synced_at: string;
  synced_by: string | null;
}

export interface VaultFilesResult {
  files: VaultFileEntry[];
}

export const testCockpitApi = {
  listRuns: () =>
    api.get<{ success: boolean; data: TestRun[] }>('/test-cockpit/runs').then(r => r.data),

  listFindings: (runId: string) =>
    api.get<{ success: boolean; data: TestFinding[] }>(`/test-cockpit/runs/${runId}/findings`).then(r => r.data),

  putResult: (findingId: string, body: { status?: string; comment?: string; admin_comment?: string; screenshot_path?: string | null }) =>
    api.put<{ success: boolean; data: unknown }>(`/test-cockpit/findings/${findingId}/result`, body),

  adminEditResult: (resultId: string, body: { comment?: string; admin_comment?: string; status?: string }) =>
    api.put<{ success: boolean; data: unknown }>(`/test-cockpit/results/${resultId}/admin-edit`, body),

  startRun: (params: { name: string; testerIds: string[]; findingIds: string[] }) =>
    api.post<{
      success: boolean;
      data: RunFromVaultResult & { bundle: { counts: Array<{ typ: string; n: number }> }; seed_errors: string[] };
    }>(`/test-cockpit/runs/start`, {
      name: params.name,
      tester_ids: params.testerIds,
      finding_ids: params.findingIds,
    }).then(r => r.data),

  endRun: (runId: string) =>
    api.post<{ success: boolean; data: { id: string; status: string; teardown_errors: string[] } }>(
      `/test-cockpit/runs/${runId}/end`, {}
    ).then(r => r.data),

  reportTesterFinding: (runId: string, body: { title: string; description: string; admin_comment?: string; screenshot_path?: string | null }) =>
    api.post<{ success: boolean; data: { finding_id: string; master_finding_id: string; idx: number } }>(
      `/test-cockpit/runs/${runId}/tester-finding`, body
    ).then(r => r.data),

  triageSummary: (runId: string) =>
    api.get<{ success: boolean; data: { markdown: string; run_name: string; finding_count: number } }>(
      `/test-cockpit/runs/${runId}/triage-summary`
    ).then(r => r.data),

  setTesterChangelog: (runId: string, testerChangelog: string | null) =>
    api.put<{ success: boolean; data: { id: string; tester_changelog: string | null } }>(
      `/test-cockpit/runs/${runId}/changelog`, { tester_changelog: testerChangelog }
    ).then(r => r.data),

  generateChangelog: (runId: string) =>
    api.post<{ success: boolean; data: { tester_changelog: string | null; finding_count?: number; message?: string } }>(
      `/test-cockpit/runs/${runId}/generate-changelog`, {}
    ).then(r => r.data),

  syncVault: (runId: string, retest = true) =>
    api.post<{
      success: boolean;
      data: {
        changed_files: Array<{ name: string; content: string; blocks_inserted: number }>;
        blocks_inserted: number;
        retest_reset: number;
        message?: string;
      };
    }>(`/test-cockpit/runs/${runId}/sync-vault`, { retest }).then(r => r.data),

  // ===== Master-Findings (DB statt Vault) =====
  listMasterFindings: () =>
    api.get<{ success: boolean; data: MasterFinding[] }>(`/test-cockpit/findings`).then(r => r.data),

  createMasterFinding: (body: Partial<MasterFinding>) =>
    api.post<{ success: boolean; data: MasterFinding }>(`/test-cockpit/findings`, body).then(r => r.data),

  updateMasterFinding: (id: string, body: Partial<MasterFinding>) =>
    api.put<{ success: boolean; data: MasterFinding }>(`/test-cockpit/findings/${id}`, body).then(r => r.data),

  deleteMasterFinding: (id: string) =>
    api.delete<{ success: boolean }>(`/test-cockpit/findings/${id}`),

  addFixVersion: (findingId: string, body: { fix_text: string; commit_hash?: string; fix_date?: string }) =>
    api.post<{ success: boolean; data: FindingFixVersion }>(`/test-cockpit/findings/${findingId}/fix-versions`, body).then(r => r.data),

  addAdminComment: (findingId: string, body: { comment_text: string; comment_date?: string; sort_order?: number }) =>
    api.post<{ success: boolean; data: FindingAdminComment }>(`/test-cockpit/findings/${findingId}/admin-comments`, body).then(r => r.data),

  addTestPath: (findingId: string, body: { stage?: string; persona_key?: string; url_path: string; hint?: string; expectation?: string; sort_order?: number }) =>
    api.post<{ success: boolean; data: FindingTestPath }>(`/test-cockpit/findings/${findingId}/test-paths`, body).then(r => r.data),

  deleteFixVersion: (id: string) =>
    api.delete<{ success: boolean }>(`/test-cockpit/finding-fix-versions/${id}`),

  deleteAdminComment: (id: string) =>
    api.delete<{ success: boolean }>(`/test-cockpit/finding-admin-comments/${id}`),

  deleteTestPath: (id: string) =>
    api.delete<{ success: boolean }>(`/test-cockpit/finding-test-paths/${id}`),

  importVault: (overwrite = false) =>
    api.post<{ success: boolean; data: { findings: number; fix_versions: number; admin_comments: number; test_paths: number } }>(`/test-cockpit/findings/import-vault`, { overwrite }).then(r => r.data),

  aggregate: (runId: string) =>
    api.get<{ success: boolean; data: AggregateFinding[] }>(`/test-cockpit/runs/${runId}/aggregate`).then(r => r.data),

  triage: (findingId: string, body: { triage_status: string; triage_note?: string }) =>
    api.post<{ success: boolean; data: unknown }>(`/test-cockpit/findings/${findingId}/triage`, body),

  retest: (findingId: string) =>
    api.post<{ success: boolean; data: { reset: number } }>(`/test-cockpit/findings/${findingId}/retest`, {}),

  uploadScreenshot: async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('file', file);
    // authFetch statt rohem fetch: Datei wird vor dem Senden speichergestützt (iOS-26.5-Bug WebKit 319985, Jörg 2026-10-06)
    const res = await authFetch('/api/test-cockpit/screenshots', { method: 'POST', body: fd });
    if (!res.ok) throw new Error('Upload fehlgeschlagen');
    const data = await res.json() as { success: boolean; data: { path: string } };
    return data.data.path;
  },

  stats: (runId: string) =>
    api.get<{ success: boolean; data: RunStats }>(`/test-cockpit/runs/${runId}/stats`).then(r => r.data),

  cloneRun: (runId: string, body: { name: string; tester_ids?: string[]; only?: 'all' | 'open' | 'fail' | 'unreleased' }) =>
    api.post<{ success: boolean; data: CloneRunResult }>(`/test-cockpit/runs/${runId}/clone`, body).then(r => r.data),

  bundleReset: () =>
    api.post<{ success: boolean; data: BundleResetResult }>(`/test-cockpit/bundle/reset`, {}).then(r => r.data),

  blockApprovePreview: (runId: string) =>
    api.post<{ success: boolean; data: { candidate_count: number; master_finalize_count: number; titles: string[] } }>(
      `/test-cockpit/runs/${runId}/block-approve?dryRun=1`, {}
    ).then(r => r.data),

  blockApprove: (runId: string, note?: string) =>
    api.post<{ success: boolean; data: { approved_count: number; master_finalized_count: number; titles: string[] } }>(
      `/test-cockpit/runs/${runId}/block-approve`, { note }
    ).then(r => r.data),

  screenshotsCleanup: (olderThanDays = 14) =>
    api.post<{ success: boolean; data: ScreenshotCleanupResult }>(`/test-cockpit/screenshots/cleanup`, { older_than_days: olderThanDays }).then(r => r.data),

  listTesters: () =>
    api.get<{ success: boolean; data: Tester[] }>(`/test-cockpit/testers`).then(r => r.data),

  listVaultFiles: () =>
    api.get<{ success: boolean; data: VaultFilesResult }>(`/test-cockpit/vault/files`).then(r => r.data),

  viewAsState: () =>
    api.get<{ success: boolean; data: { active: boolean; targetUserId?: string } }>(`/test-cockpit/view-as/state`).then(r => r.data),

  viewAsStart: (testerId: string) =>
    api.post<{ success: boolean; data: { active: boolean; targetUserId: string } }>(`/test-cockpit/view-as/start`, { tester_id: testerId }).then(r => r.data),

  viewAsStop: () =>
    api.post<{ success: boolean; data: { active: boolean } }>(`/test-cockpit/view-as/stop`, {}).then(r => r.data),

  claudeDesktopState: () =>
    api.get<{ success: boolean; data: ClaudeDesktopState }>(`/test-cockpit/claude-desktop`).then(r => r.data),

  claudeDesktopSet: (active: boolean) =>
    api.put<{ success: boolean; data: ClaudeDesktopState & { changed: number; sessionsEnded: number } }>(`/test-cockpit/claude-desktop`, { active }).then(r => r.data),
};

/** Claude-Desktop-Zugang (Jörg 2026-10-28): Test-Personas claude-*@test.* */
export interface ClaudeDesktopAccount {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  auth_active: boolean | null;
  locked: boolean | null;
  last_login_at: string | null;
  sessions: number;
}

export interface ClaudeDesktopState {
  active: boolean;
  accounts: ClaudeDesktopAccount[];
}
